import React, { useEffect, useMemo, useState } from "react"

import FilterForm from "@/components/FilterForm/FilterForm"
import { FilterSelectState } from "@/components/FilterForm/FilterFormRow"
import Icon from "@/components/Icon"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { allReasons } from "@/config/stageDialogConfig"
import { useAuth } from "@/contexts/AuthContext"
import { Depot } from "@/hooks/useDepots"
import { FilterState } from "@/types/application"
import { JobPosting } from "@/types/jobPosting"
import { DEFAULT_APPLICATION_FILTERS } from "@/utils/applicationFilterState"
import { FilterProperty, FilterType, FilterVariant } from "@/utils/filtering"

import { stagesConfig } from "./job-postings/KanbanStageConfig"

const BOOKED_FILTERS = ["orientationBooked", "trialBooked", "vehicleBooked"] as const

const PERSON_STATUS_OPTIONS = ["Active", "Inactive", "Banned", "Applied"]

const STAGE_REASON_LABELS: Record<string, string> = Object.fromEntries(
  allReasons.map((r) => [r.value, r.label]),
)

const STAGE_LABELS: Record<string, string> = Object.fromEntries(
  stagesConfig.map((s) => [s.stage, s.label]),
)

const toSelectsStates = (filters: FilterState): FilterSelectState[] => {
  const selects: FilterSelectState[] = []
  if (filters.location !== DEFAULT_APPLICATION_FILTERS.location) {
    selects.push({ id: "location", filterType: FilterType.Is, value: filters.location })
  }
  if (filters.jobPostings.length) {
    selects.push({
      id: "jobPosting",
      filterType: FilterType.Is,
      value: filters.jobPostings,
    })
  }
  for (const id of BOOKED_FILTERS) {
    if (filters[id] !== null) {
      selects.push({ id, filterType: FilterType.Is, value: filters[id] ? "Yes" : "No" })
    }
  }
  if (filters.personStatus) {
    selects.push({ id: "personStatus", filterType: FilterType.Is, value: filters.personStatus })
  }
  if (filters.stage) {
    selects.push({ id: "stage", filterType: FilterType.Is, value: filters.stage })
  }
  if (filters.stageReason) {
    selects.push({ id: "stageReason", filterType: FilterType.Is, value: filters.stageReason })
  }
  return selects
}

const fromSelectsStates = (selects: FilterSelectState[]): FilterState => {
  const valueOf = (id: string) => {
    const value = selects.find((s) => s.id === id)?.value
    return typeof value === "string" && value ? value : undefined
  }
  const yesNo = (id: string) => {
    const value = valueOf(id)
    return value ? value === "Yes" : null
  }
  return {
    location: valueOf("location") ?? DEFAULT_APPLICATION_FILTERS.location,
    jobPostings: [
      ...new Set(
        selects
          .filter((s) => s.id === "jobPosting")
          .flatMap((s) => (Array.isArray(s.value) ? s.value : [])),
      ),
    ],
    orientationBooked: yesNo("orientationBooked"),
    trialBooked: yesNo("trialBooked"),
    vehicleBooked: yesNo("vehicleBooked"),
    personStatus: valueOf("personStatus") ?? null,
    stage: valueOf("stage") ?? null,
    stageReason: valueOf("stageReason") ?? null,
  }
}

interface FilterBarProps {
  locations: Depot[]
  jobPostings: JobPosting[]
  filters: FilterState
  setFilters: (filters: FilterState) => void
  onSearch: (term: string) => void
  isLoading?: boolean
}

const FilterBar: React.FC<FilterBarProps> = ({
  locations = [],
  jobPostings = [],
  filters,
  setFilters,
  onSearch,
  isLoading = false,
}) => {
  const { user, claims } = useAuth()
  const isDSP = claims.user_role === "DSP"
  const [localSearch, setLocalSearch] = useState("")
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [selectsStates, setSelectsStates] = useState<FilterSelectState[]>([])

  useEffect(() => {
    if (!filtersOpen) {
      const selects = toSelectsStates(filters)
      setSelectsStates(
        selects.length ? selects : [{ id: "", filterType: FilterType.Is, value: "" }],
      )
    }
  }, [filtersOpen, filters])

  // Filter locations and job postings for DSP users based on their dsp_id
  const filteredJobPostings = useMemo(() => {
    if (!isDSP || !user?.dsp_id) {
      return jobPostings
    }
    return jobPostings.filter((jp) => jp.dsp_id === user.dsp_id)
  }, [isDSP, user?.dsp_id, jobPostings])

  const filteredLocations = useMemo(() => {
    if (!isDSP || !user?.dsp_id) {
      return locations
    }
    const dspLocationIds = new Set(filteredJobPostings.map((jp) => jp.depot_id).filter(Boolean))
    return locations.filter((loc) => dspLocationIds.has(loc.id))
  }, [isDSP, user?.dsp_id, locations, filteredJobPostings])

  const filterProperties = useMemo(() => {
    const props: FilterProperty[] = [
      {
        id: "location",
        label: "Location",
        filterVariant: FilterVariant.Select,
        filterTypes: [FilterType.Is],
        filterOptions: (filteredLocations || []).map((d) => d.id),
        filterOptionLabels: Object.fromEntries(
          (filteredLocations || []).map((d) => [d.id, `${d.name} (${d.city})`]),
        ),
      },
      {
        id: "jobPosting",
        label: "Job Posting",
        filterVariant: FilterVariant.MultiSelect,
        filterTypes: [FilterType.Is],
        filterOptions: [...filteredJobPostings]
          .sort((a, b) => Number(b.active) - Number(a.active) || a.title.localeCompare(b.title))
          .map((jp) => jp.id),
        filterOptionLabels: Object.fromEntries(
          filteredJobPostings.map((jp) => [
            jp.id,
            `${jp.title} (${jp.active ? "Active" : "Inactive"})`,
          ]),
        ),
      },
      {
        id: "orientationBooked",
        label: "Orientation Booked",
        filterVariant: FilterVariant.Select,
        filterTypes: [FilterType.Is],
        filterOptions: ["Yes", "No"],
      },
      {
        id: "trialBooked",
        label: "Trial Shift Booked",
        filterVariant: FilterVariant.Select,
        filterTypes: [FilterType.Is],
        filterOptions: ["Yes", "No"],
      },
      {
        id: "vehicleBooked",
        label: "Vehicle Collection Booked",
        filterVariant: FilterVariant.Select,
        filterTypes: [FilterType.Is],
        filterOptions: ["Yes", "No"],
      },
      {
        id: "personStatus",
        label: "Person Status",
        filterVariant: FilterVariant.Select,
        filterTypes: [FilterType.Is],
        filterOptions: PERSON_STATUS_OPTIONS,
      },
      {
        id: "stage",
        label: "Application Stage",
        filterVariant: FilterVariant.Select,
        filterTypes: [FilterType.Is],
        filterOptions: Object.keys(STAGE_LABELS),
        filterOptionLabels: STAGE_LABELS,
      },
      {
        id: "stageReason",
        label: "Stage Reason",
        filterVariant: FilterVariant.Select,
        filterTypes: [FilterType.Is],
        filterOptions: Object.keys(STAGE_REASON_LABELS),
        filterOptionLabels: STAGE_REASON_LABELS,
      },
    ]
    return props
  }, [filteredLocations, filteredJobPostings])

  const activeFilterCount = toSelectsStates(filters).length

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") onSearch(localSearch)
  }

  const clearSearch = () => {
    setLocalSearch("")
    onSearch("")
  }

  const handleApplyFilters = (filterStates: FilterSelectState[]) => {
    setFilters(fromSelectsStates(filterStates))
    setFiltersOpen(false)
  }

  return (
    <div className="flex justify-between flex-1">
      <div className="min-w-80 w-1/2 relative">
        <Icon
          name="magnifyingGlass"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-palette-jungleGreen400 h-4 w-4"
        />
        <Input
          placeholder="Search by name, email, person ID or application ID"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          className="pl-10 pr-8 h-10 border-palette-jungleGreen200 focus:border-palette-skyBlue500 focus:ring-palette-skyBlue500"
        />
        {localSearch && (
          <button
            onClick={clearSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-palette-jungleGreen400 hover:text-palette-jungleGreen600 transition-colors"
            aria-label="Clear search"
          >
            <Icon name="close" className="h-4 w-4" />
          </button>
        )}
      </div>

      <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              disabled={isLoading}
              className="h-10 gap-2 rounded-full border-palette-jungleGreen200 px-5"
            >
              <Icon name="filter" className="h-4 w-4 text-palette-jungleGreen700" />
              Filters
            </Button>
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 h-5 min-w-5 rounded-full bg-palette-jungleGreen900 text-white text-xs flex items-center justify-center px-1">
                {activeFilterCount}
              </span>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent className="lg:w-[48rem] w-[80vw] p-4" align="end">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-palette-jungleGreen900">Filters</h4>
            <button
              onClick={() => setFiltersOpen(false)}
              className="text-palette-jungleGreen400 hover:text-palette-jungleGreen600"
            >
              <Icon name="close" className="h-4 w-4" />
            </button>
          </div>
          <FilterForm
            selectsStates={selectsStates}
            setSelectsStates={setSelectsStates}
            onApply={handleApplyFilters}
            properties={filterProperties}
            fixedColumns
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default FilterBar
